import { useState, useEffect } from 'react';
import { openSunmiDrawer } from '../../../utils/sunmiPrinter';

interface OpenDrawerModalProps {
  onClose: () => void;
  onDrawerOpened?: (payload: { reason: string; note?: string }) => void;
}

export default function OpenDrawerModal({ onClose, onDrawerOpened }: OpenDrawerModalProps) {
  const [step, setStep] = useState<'reason' | 'pin' | 'opening' | 'success'>('reason');
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const reasons = [
    { id: 'change', label: 'Make Change', icon: 'ri-exchange-dollar-line', color: 'bg-blue-500' },
    { id: 'deposit', label: 'Cash Deposit', icon: 'ri-safe-line', color: 'bg-green-500' },
    { id: 'withdrawal', label: 'Cash Withdrawal', icon: 'ri-hand-coin-line', color: 'bg-orange-500' },
    { id: 'count', label: 'Cash Count', icon: 'ri-calculator-line', color: 'bg-purple-500' },
    { id: 'check', label: 'Drawer Check', icon: 'ri-search-line', color: 'bg-teal-500' },
    { id: 'other', label: 'Other Reason', icon: 'ri-more-line', color: 'bg-gray-500' },
  ];

  const handleReasonSelect = (reasonId: string) => {
    setSelectedReason(reasonId);
    setError('');
  };

  const handleContinue = () => {
    if (!selectedReason) {
      setError('Please select a reason');
      return;
    }
    if (selectedReason === 'other' && !customReason.trim()) {
      setError('Please specify the reason');
      return;
    }
    setStep('pin');
    setError('');
  };

  const handlePinInput = (digit: string) => {
    if (pin.length < 4) {
      setPin(pin + digit);
      setError('');
    }
  };

  const handlePinClear = () => {
    setPin('');
    setError('');
  };

  const handlePinDelete = () => {
    setPin(pin.slice(0, -1));
    setError('');
  };

  const handleVerifyPin = () => {
    // Demo: Accept PIN 1234 or 9999
    if (pin === '1234' || pin === '9999') {
      setStep('opening');
      setError('');
      
      // Open physical drawer and simulate UI delay
      openSunmiDrawer();
      setTimeout(() => {
        setDrawerOpen(true);
        setStep('success');
      }, 2000);
    } else {
      setError('Invalid supervisor PIN');
      setPin('');
    }
  };

  const handleClose = () => {
    if (drawerOpen) {
      const reason = reasons.find(r => r.id === selectedReason);
      onDrawerOpened?.({
        reason: selectedReason === 'other' ? customReason : reason?.label || 'Open Drawer',
        note: selectedReason === 'other' ? customReason : undefined,
      });
    }
    onClose();
  };

  useEffect(() => {
    if (pin.length === 4) {
      handleVerifyPin();
    }
  }, [pin]);

  if (step === 'opening') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] min-h-[300px] overflow-y-auto p-8 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-blue-500 text-white rounded-full mx-auto mb-4 animate-pulse">
            <i className="ri-safe-2-line text-5xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 whitespace-nowrap">Opening Cash Drawer</h2>
          <p className="text-gray-600 mb-4 whitespace-nowrap">Please wait while the drawer opens...</p>
          <div className="flex items-center justify-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] min-h-[300px] overflow-y-auto p-8 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-green-500 text-white rounded-full mx-auto mb-4">
            <i className="ri-check-line text-5xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 whitespace-nowrap">Drawer Opened</h2>
          <p className="text-gray-600 mb-6 whitespace-nowrap">Cash drawer is now open</p>
          
          <div className="p-4 bg-gray-50 rounded-lg mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 whitespace-nowrap">Reason:</span>
              <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                {selectedReason === 'other' ? customReason : reasons.find(r => r.id === selectedReason)?.label}
              </span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 whitespace-nowrap">Time:</span>
              <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                {new Date().toLocaleTimeString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 whitespace-nowrap">Authorized By:</span>
              <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">Supervisor</span>
            </div>
          </div>

          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-6">
            <div className="flex items-start gap-2">
              <i className="ri-alert-line text-yellow-600 text-xl mt-0.5"></i>
              <div className="text-left">
                <p className="text-sm font-semibold text-yellow-900 mb-1 whitespace-nowrap">Important Reminder</p>
                <p className="text-xs text-yellow-800 whitespace-nowrap">Please close the drawer securely after completing your task</p>
              </div>
            </div>
          </div>

          <button
            onClick={handleClose}
            className="w-full px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-bold whitespace-nowrap transition-colors cursor-pointer"
          >
            <i className="ri-check-line mr-2"></i>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (step === 'pin') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] min-h-[300px] overflow-y-auto flex flex-col">
          <div className="bg-orange-600 text-white px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold whitespace-nowrap">Supervisor Authorization</h2>
              <p className="text-sm text-orange-100 whitespace-nowrap">Enter supervisor PIN to open drawer</p>
            </div>
            <button 
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center hover:bg-orange-700 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div className="p-6">
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 flex items-center justify-center ${reasons.find(r => r.id === selectedReason)?.color} text-white rounded-lg`}>
                  <i className={`${reasons.find(r => r.id === selectedReason)?.icon} text-xl`}></i>
                </div>
                <div>
                  <p className="text-xs text-gray-600 whitespace-nowrap">Opening drawer for:</p>
                  <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                    {selectedReason === 'other' ? customReason : reasons.find(r => r.id === selectedReason)?.label}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3 whitespace-nowrap">Enter Supervisor PIN</label>
              <div className="flex items-center justify-center gap-3 mb-4">
                {[0, 1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className={`w-14 h-14 flex items-center justify-center border-2 rounded-lg text-2xl font-bold transition-all ${
                      pin.length > index
                        ? 'border-orange-500 bg-orange-50 text-orange-600'
                        : 'border-gray-300 bg-white text-gray-400'
                    }`}
                  >
                    {pin.length > index ? '●' : '○'}
                  </div>
                ))}
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 mb-4">
                  <i className="ri-error-warning-line text-red-600 text-xl"></i>
                  <p className="text-sm text-red-800 whitespace-nowrap">{error}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                  <button
                    key={digit}
                    onClick={() => handlePinInput(digit.toString())}
                    className="h-14 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg text-xl font-bold transition-colors cursor-pointer"
                  >
                    {digit}
                  </button>
                ))}
                <button
                  onClick={handlePinClear}
                  className="h-14 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Clear
                </button>
                <button
                  onClick={() => handlePinInput('0')}
                  className="h-14 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg text-xl font-bold transition-colors cursor-pointer"
                >
                  0
                </button>
                <button
                  onClick={handlePinDelete}
                  className="h-14 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-delete-back-2-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-blue-600 text-lg mt-0.5"></i>
                <p className="text-xs text-blue-800 whitespace-nowrap">Demo PIN: 1234 or 9999</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
            <button
              onClick={() => setStep('reason')}
              className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] min-h-[300px] overflow-y-auto flex flex-col">
        <div className="bg-orange-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold whitespace-nowrap">Open Cash Drawer</h2>
            <p className="text-sm text-orange-100 whitespace-nowrap">Select reason for opening the drawer</p>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-orange-700 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            {reasons.map((reason) => (
              <button
                key={reason.id}
                onClick={() => handleReasonSelect(reason.id)}
                className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  selectedReason === reason.id
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 flex items-center justify-center ${reason.color} text-white rounded-lg`}>
                    <i className={`${reason.icon} text-2xl`}></i>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{reason.label}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {selectedReason === 'other' && (
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">Specify Reason</label>
              <input
                type="text"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Enter reason for opening drawer"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                autoFocus
              />
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 mb-6">
              <i className="ri-error-warning-line text-red-600 text-xl"></i>
              <p className="text-sm text-red-800 whitespace-nowrap">{error}</p>
            </div>
          )}

          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <i className="ri-shield-check-line text-yellow-600 text-xl mt-0.5"></i>
              <div>
                <p className="text-sm font-semibold text-yellow-900 mb-1 whitespace-nowrap">Security Notice</p>
                <p className="text-xs text-yellow-800 whitespace-nowrap">Opening the cash drawer requires supervisor authorization. This action will be logged for audit purposes.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
          >
            Cancel
          </button>
          
          <button
            onClick={handleContinue}
            disabled={!selectedReason || (selectedReason === 'other' && !customReason.trim())}
            className={`px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
              !selectedReason || (selectedReason === 'other' && !customReason.trim())
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-orange-600 hover:bg-orange-700 text-white cursor-pointer'
            }`}
          >
            <i className="ri-arrow-right-line mr-2"></i>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
